// Pure helpers for presenting a single alarm/fault, ported from the app's
// Alarms List + AlarmDetails so the web shows the same headings, titles,
// resolution guidance and contact numbers. No darwin_core state here — callers
// pass the already-selected fault object and installer number.

// darwin_core/utils/consts.js IDEAL_SUPPORT_NUMBER (dial string) + its display form.
export const IDEAL_SUPPORT_NUMBER = '01482498660';
export const IDEAL_SUPPORT_DISPLAY = '01482 498660';

// The short code shown as the entry's heading — the enriched faultCode, else the
// raw type.
const faultCodeOf = (fault) => fault?.faultCode || fault?.type;

// A stable id for deep-linking one active fault. raisedAt alone is NOT unique — a
// connectivity drop can raise several faults in one backend-timestamped batch, so
// two active faults can share a raisedAt — hence combine it with the fault
// code/type, matching how the app disambiguates (getAlarm keys on
// type+faultCode+raisedAt; the Alarms list keys on raisedAt+faultCode). Both the
// deep link and the detail-screen lookup go through this so they always agree.
const alarmId = (fault) => {
  const raised = fault?.raisedAt != null ? String(fault.raisedAt) : '';
  return `${raised}~${faultCodeOf(fault) ?? ''}`;
};

// The list heading (app Alarms/List Active tab): "<code>" or, when the fault is
// tied to a named zone, "<code> - <zone>". Falls back to "Fault" for a malformed
// fault with neither a code nor a type so the UI never prints "undefined".
export function alarmHeadText(fault) {
  const code = faultCodeOf(fault) || 'Fault';
  return fault?.zoneFriendlyName ? `${code} - ${fault.zoneFriendlyName}` : code;
}

// The detail-screen title (app AlarmDetails): a service-due fault uses the head
// text; everything else is "<description> - <code/type>". Guarded so a fault
// missing one or both fields never renders "undefined - undefined" (in the <h1>
// and, via useScreen, the document title).
export function alarmTitle(fault) {
  if (fault?.type === 'HaloServiceDue') {
    return alarmHeadText(fault);
  }
  const description = fault?.description;
  const code = faultCodeOf(fault);
  if (description && code) {
    return `${description} - ${code}`;
  }
  return description || code || 'Fault';
}

// Whether the PRT carries an installer number that is not Ideal's own support
// line (drives the "Service Engineer" contact row).
export function installerIsNotIdeal(installerNumber) {
  return Boolean(installerNumber) && !/498660/.test(installerNumber);
}

// The resolution guidance (app AlarmDetails): a service-due fault shows its own
// description; otherwise prefer the fault's own resolutionText, then fall back to
// naming the installer (if any) plus Ideal, else just Ideal.
export function alarmResolutionText(fault, installerNumber) {
  if (fault?.type === 'HaloServiceDue') {
    return fault.description;
  }
  const idealOnly = 'Please call Ideal customer support on 01482 498660';
  if (fault?.resolutionText && fault.resolutionText !== idealOnly) {
    return fault.resolutionText;
  }
  if (installerIsNotIdeal(installerNumber)) {
    return `Please call your service engineer on ${installerNumber} or Ideal customer support on 01482 498660`;
  }
  return idealOnly;
}

// getAlarmsBySeverity rebuilds its array AND getControlFaults freshly clones each
// fault on every call, so a reference/shallow comparator churns; compare by the
// fields the UI renders so an unchanged fault set is treated as equal (a stable
// useSelector result, no re-render, no react-redux dev warning).
export function sameFaults(a, b) {
  if (a === b) {
    return true;
  }
  if (a.length !== b.length) {
    return false;
  }
  return a.every((f, i) => {
    const g = b[i];
    return (
      f.faultCode === g.faultCode &&
      f.severity === g.severity &&
      f.description === g.description &&
      f.type === g.type &&
      f.raisedAt === g.raisedAt
    );
  });
}

export { alarmId, faultCodeOf };
